import { matchPath } from 'react-router-dom';
import type { TeamType } from '@modules/Auth/models/Auth';

type Expression = { all: readonly string[] } | { any: readonly string[] } | 'member' | 'admin' | 'denied';
interface Policy {
  tenant_3pl: Expression;
  tenant_external: Expression;
}
const policies = new Map<string, Policy>();
function register(paths: string[], threepl: Expression, external: Expression = 'denied') {
  for (const path of paths) policies.set(path, { tenant_3pl: threepl, tenant_external: external });
}
register(
  ['wholesale-orders', 'wholesale-orders/:id', 'wholesale-orders/:id/labels'],
  { all: ['access.wholesale-orders.view'] },
  { all: ['access.orders.view'] },
);
register(['wholesale-orders/new'], { all: ['access.wholesale-orders.manage'] }, { all: ['access.orders.manage'] });
register(['wholesale-pickups', 'wholesale-pickups/:id'], { all: ['access.wholesale-orders.view'] });
register(['wholesale-preparations', 'wholesale-orders/:id/preparation'], { all: ['access.wholesale-orders.view'] });
// Each entry names the destination's approved capability. Legacy declarations remain separate.
register(['', 'home', 'configuration/profile', 'configuration/notifications', 'verify-new-email'], 'member', 'member');
register(['configuration/roles'], 'admin', 'admin');
register(['configuration/users'], { all: ['access.users.view'] }, { all: ['access.users.view'] });
register(
  [
    'orders',
    'orders/view/:orderId',
    'orders/crm',
    'orders/crm/view/:orderId',
    'incidents',
    'orders-shipped',
    'reports/warehouse/ready-to-prepare-by-hour',
  ],
  { all: ['access.orders.view'] },
  { all: ['access.orders.view'] },
);
register(
  ['orders/create', 'orders/edit/:id', 'crm/management-hours'],
  { all: ['access.orders.manage'] },
  { all: ['access.orders.manage'] },
);
register(['orders/shipment-status-transitions'], {
  all: ['access.orders.manage', 'access.orders.resolve-status-transitions'],
});
register(['products', 'packs'], { all: ['access.products.view'] }, { all: ['access.products.view'] });
register(['packaging'], { all: ['access.products.view'] });
register(['products/create'], { all: ['access.products.manage'] }, { all: ['access.products.manage'] });
register(
  [
    'batches',
    'batches/:id/*',
    'configuration/product-categories',
    'configuration/warehouse-materials',
    'clients/:clientId/packaging-recipes',
    'clients/:clientId/packaging-recipes/:recipeId',
  ],
  { all: ['access.products.view'] },
);
register(['shops', 'shops/import-history/:shopId?'], { all: ['access.shops.view'] }, { all: ['access.shops.view'] });
register(['merchandise/*'], { all: ['access.entries.view'] }, { all: ['access.entries.view'] });
register(['receptions', 'receptions/:id', 'unreferenced-entries', 'unreferenced-entries/:id'], {
  all: ['access.receptions.view'],
});
register(
  [
    'locations',
    'merchandise-blocks',
    'merchandise-blocks/:id',
    'warehouse/stock-movements',
    'carts',
    'location-movements',
    'location-orders',
    'location-orders/:id',
    'relocations',
    'relocations/:id',
    'warehouses',
    'warehouses/:id',
  ],
  { all: ['access.inventory.view'] },
);
register(['relocations/new'], { all: ['access.inventory.manage'] });
register(
  ['stock-movements', 'products-without-stock', 'reports/inventory'],
  { all: ['access.inventory.view'] },
  { all: ['access.products.view'] },
);
register(['pending-orders', 'held-for-preparation', 'order-preparation'], { all: ['access.preparations.view'] });
register(
  [
    'order-preparation/picking/:id',
    'order-preparation/packing',
    'order-preparation/packing/:id',
    'order-preparation/massive-packing/:id',
  ],
  { all: ['access.preparations.manage'] },
);
register(['order-preparation/manifests'], { all: ['access.manifests.view'] });
register(['extra-work-orders', 'extra-work-orders/:id'], { all: ['access.extra-work.view'] });
register(
  ['cross-docking/operations', 'cross-docking/operations/:operationId', 'cross-docking/receptions/:receptionId'],
  { all: ['access.cross-docking.view'] },
);
register(
  [
    'cross-docking/operations/new',
    'cross-docking/operations/edit/:operationId',
    'cross-docking/receptions/:receptionId/receiving',
    'cross-docking/receptions/:receptionId/packing/:sessionId',
  ],
  { all: ['access.cross-docking.manage'] },
);
register(['refunds', 'refunds/:id/detail'], { all: ['access.refunds.view'] }, { all: ['access.refunds.view'] });
register(['refund-receptions', 'returned'], { all: ['access.refunds.view'] });
register(['refunds/:id/receive', 'refund-receptions/:id/receive', 'returned/:id/receive', 'returned/:id/locate'], {
  all: ['access.refunds.manage'],
});
register(['customer-hero/inbox', 'customer-hero/inbox/:conversationId'], { all: ['access.customer-hero.view'] });
register(['customer-hero/mailboxes'], { all: ['access.customer-hero.view'] });
register(['customer-hero/mailboxes/callback'], {
  all: ['access.customer-hero.manage', 'access.customer-hero.manage-mailboxes'],
});
register(['customer-hero/metrics'], { all: ['access.customer-hero.view'] });
register(['customer-hero/templates'], { all: ['access.customer-hero.view'] });
register(['customer-hero/agents'], 'admin');
register(['reused'], { all: ['access.refused-orders.view'] });
register(['reused/:id/receive', 'reused/identify-app'], { all: ['access.refused-orders.manage'] });
register(['warehouse/productivity', 'warehouse/dashboard', 'reused/dashboard'], { all: ['access.productivity.view'] });
register(['clients', 'clients/:id', 'reports/client-activity', 'reports/client-activity/clients/:clientId/orders'], {
  all: ['access.clients.view'],
});
register(['clients/create'], { all: ['access.clients.manage'] });
register(['configuration/popups/*'], { all: ['access.clients.manage', 'access.clients.manage-notices'] });
register(
  [
    'clients/:clientId/rules',
    'clients/:clientId/rules/:id',
    'configuration/shipping-rule-presets',
    'configuration/shipping-rule-presets/history',
    'configuration/shipping-rule-presets/:presetId/edit',
  ],
  { all: ['access.shipping-rules.view'] },
);
register(
  ['clients/:clientId/rules/create', 'clients/:clientId/rules/:id/edit', 'configuration/shipping-rule-presets/create'],
  { all: ['access.shipping-rules.manage'] },
);
register(['packing-stations', 'configuration/printers'], {
  all: ['access.configuration.manage-printers-stations'],
});
register(['locations/staging-zones', 'configuration/wholesale-staging-zones'], {
  all: ['access.wholesale-orders.view'],
});
register(['configuration/preparation-calendar'], { all: ['access.configuration.manage-preparation-calendar'] });
register(
  [
    'configuration/couriers',
    'configuration/coverage-zones',
    'configuration/shipment-status-mapping',
    'configuration/tracking-monitoring',
    'configuration/tracking-files',
    'logistics/sla/alerts',
    'logistics/sla/claims',
    'logistics/sla/config/rules',
    'logistics/sla/config/false-delivery',
    'logistics/sla/config/breach-types',
    'logistics/couriers/tracking-failures',
  ],
  { all: ['access.transport.view'] },
);
register(
  ['invoices', 'invoices/view/:preInvoiceId', 'invoices/eligible-shipments'],
  { all: ['access.invoices.view'] },
  { all: ['access.invoices.view'] },
);
register(
  [
    'invoices/eligible-extra-work',
    'invoices/eligible-crm-management',
    'invoices/eligible-wholesale-preparation',
    'invoices/eligible-storage',
    'invoices/shipment-credits',
    'invoices/minimum_fees',
    'invoices/pre-invoices',
    'invoices/pre-invoices/view/:preInvoiceId',
    'accounting/data-load',
  ],
  { all: ['access.invoices.view'] },
);
register(['invoices/rates', 'invoices/rates/:id'], { all: ['access.rates-contracts.view'] });
register(['invoices/rates/new'], { all: ['access.rates-contracts.manage'] });
register(
  [
    'wallets',
    'wallets/:id',
    'wallets/withdrawals',
    'wallets/bank-accounts',
    'wallets/topups',
    'withdrawal-payment-files',
    'cod-payments/payouts',
    'cod-payments/payouts/:id',
    'cod-payments/payout-items',
    'cod-payments/courier-files',
    'cod-payments/courier-files/:id',
    'cod-payments/mismatches',
    'cod-payments/reviews',
    'reports/cod-payments',
  ],
  { all: ['access.balances.view'] },
  { all: ['access.balances.view'] },
);
register(['reports/wallet-accounting-sync'], { all: ['access.balances.view'] });
register(['accounting/dashboard'], {
  any: ['access.invoices.view', 'access.balances.view'],
});
register(['analytical-accounting/cost-centers'], { all: ['access.analytical-accounting.view'] });
register(['accounting/affiliate-partners', 'accounting/affiliate-commissions'], { all: ['access.affiliates.view'] });
register(['configuration/affiliates'], 'denied', { all: ['access.my-commissions.view'] });
register(
  ['reports/logistics', 'reports/logistic-pivot'],
  { all: ['access.orders.view'] },
  { all: ['access.reports.view'] },
);
register(['reports/orders-without-weight'], { all: ['access.orders.view'] });
register(['reports/warehouse/excel'], { all: ['access.inventory.view'] });
register(['reports/3pl/excel'], {
  any: ['access.orders.view', 'access.clients.view', 'access.invoices.view', 'access.balances.view'],
});
register(['reports/marketing'], { all: ['access.marketing.view'] });
register(['configuration/integrations', 'configuration/webhooks', 'configuration/webhooks/:id'], 'denied', {
  all: ['access.integrations.view'],
});
register(['integrations/zendesk/tickets', 'integrations/zendesk/tickets/:ticketId'], 'denied', {
  all: ['access.support.view'],
});
// Retired or hidden screens gain no access from removing their legacy permission.
register(
  [
    'configuration/incident-mappings',
    'configuration/general',
    'configuration/suscription',
    'reports/profitability',
    'reports/profitability/orders/:orderId',
    'reports/profitability/ad-spend',
    'reports/profitability/shipping-costs',
    'reports/profitability/product-costs',
    'reports/3pl/legacy-dashboard',
  ],
  'denied',
);

export function getBlockRoutePolicy(path: string, teamType: TeamType | null): Expression {
  if (teamType !== 'tenant_3pl' && teamType !== 'tenant_external') return 'denied';
  const normalized = path.split(/[?#]/)[0].replace(/^\//, '').replace(/\/$/, '');
  const exact = policies.get(normalized);
  if (exact) return exact[teamType];
  // Only registered router patterns are matched; no permission inference by URL prefix.
  const matches = [...policies.entries()].filter(
    ([pattern]) => !!matchPath({ path: `/${pattern}`, end: true }, `/${normalized}`),
  );
  matches.sort(
    ([a], [b]) =>
      b.split('/').filter((part) => !part.startsWith(':') && part !== '*').length -
      a.split('/').filter((part) => !part.startsWith(':') && part !== '*').length,
  );
  return matches[0]?.[1][teamType] ?? 'denied';
}
export function canOpenBlockRoute(
  path: string,
  context: { teamType: TeamType | null; isAdmin: boolean; hasPermission: (slug: string) => boolean },
): boolean {
  const policy = getBlockRoutePolicy(path, context.teamType);
  if (policy === 'member') return true;
  if (policy === 'admin') return context.isAdmin;
  if (policy === 'denied') return false;
  return 'all' in policy ? policy.all.every(context.hasPermission) : policy.any.some(context.hasPermission);
}

register(['products/edit/:id'], { all: ['access.products.view'] }, { all: ['access.products.view'] });
